import { Loader } from '@googlemaps/js-api-loader';
import { Box, IconButton, InputAdornment, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CurrentLocationIcon } from '../../assets/icons';
import { addSnackbar } from '../../store/features/snackbar/snackbarSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { GoogleMapAddress } from '../../types/util';
import { AppIcon } from '../app-icon/AppIcon';
import { TextInput, TextInputProps } from '../text-input/TextInput';
import { DEFAULT_ADDRESS, GOOGLE_MAP_API_KEY } from './constants';
import { GoogleMapMarker } from './GoogleMapMarker';
import { googleMapLocationStyles } from './styles';
import './google-map-styles.css';
import { setGoogleMapAuthToError } from '../../store/features/google-map-auth/googleMapAuthSlice';
import { useFetchSiteSettingsQuery } from '../../store/features/site-setting/siteSettingSlice';
import _ from 'lodash';

interface GoogleMapLocationFieldProps extends TextInputProps {
  defaultAddress?: GoogleMapAddress;
  onChange: (address: GoogleMapAddress) => void;
  LatitudeInputProps?: React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;
  LongitudeInputProps?: React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;
}

export const GoogleMapLocationField: React.FC<GoogleMapLocationFieldProps> = ({
  defaultAddress,
  onChange,
  LatitudeInputProps,
  LongitudeInputProps,
  sx,
  ...textInputProps
}) => {
  const dispatch = useAppDispatch();
  const isGoogleMapAuthError = useAppSelector((state) => state.googleMapAuth.isError);
  const onChangeRef = useRef<typeof onChange>(onChange);
  const { siteSettingAddress } = useFetchSiteSettingsQuery(undefined, {
    selectFromResult: ({ data }) => ({
      siteSettingAddress: data?.siteSetting.site_info.site_address || DEFAULT_ADDRESS,
    }),
  });

  const mapDivRef = useRef<HTMLDivElement>(null);
  const loaderRef = useRef<Loader | null>(null);
  const autocompleteInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [currentAddress, setCurrentAddress] = useState<GoogleMapAddress>({
    location: _.defaultTo(defaultAddress?.location, siteSettingAddress.location),
    lat: _.defaultTo(defaultAddress?.lat, siteSettingAddress.lat),
    lng: _.defaultTo(defaultAddress?.lng, siteSettingAddress.lng),
  });
  const defaultAddressRef = useRef<GoogleMapAddress>(defaultAddress || currentAddress);

  // Check for auth error
  useEffect(() => {
    if (!window.gm_authFailure) {
      window.gm_authFailure = () => {
        dispatch(setGoogleMapAuthToError());
      };
    }
  }, [dispatch]);

  useEffect(() => {
    if (autocompleteInputRef.current && isGoogleMapAuthError) {
      // Removing disabled attribute is important like this since Google controls our UI state
      autocompleteInputRef.current.removeAttribute('disabled');
    }
  }, [isGoogleMapAuthError]);

  // Loads the map
  useEffect(() => {
    if (GOOGLE_MAP_API_KEY && !loaderRef.current) {
      loaderRef.current = new Loader({
        apiKey: GOOGLE_MAP_API_KEY,
        libraries: ['places'],
      });
    }

    (async () => {
      if (mapDivRef.current && loaderRef.current) {
        try {
          const google = await loaderRef.current.load();
          setMap(
            new google.maps.Map(mapDivRef.current, {
              zoom: 10,
            })
          );
          if (autocompleteInputRef.current && !autocompleteRef.current) {
            autocompleteRef.current = new google.maps.places.Autocomplete(autocompleteInputRef.current, {
              types: ['(regions)'],
              fields: ['formatted_address', 'geometry.location'],
            });
            autocompleteRef.current.addListener('place_changed', () => {
              if (autocompleteRef.current) {
                const place = autocompleteRef.current.getPlace();
                if (place.geometry?.location && place.formatted_address) {
                  const newAddress: GoogleMapAddress = {
                    location: place.formatted_address,
                    lat: place.geometry.location.lat(),
                    lng: place.geometry.location.lng(),
                  };
                  setCurrentAddress(newAddress);
                }
              }
            });
          }
        } catch (error) {
          console.error(error);
        }
      }
    })();
  }, []);

  // Setting the center of the map based on current location
  useEffect(() => {
    if (map) {
      map.setCenter({
        lat: currentAddress.lat,
        lng: currentAddress.lng,
      });
    }
  }, [currentAddress, map]);

  // Listen to change in current location and default location
  useEffect(() => {
    if (autocompleteInputRef.current) {
      autocompleteInputRef.current.value = currentAddress.location;
    }
    onChangeRef.current(currentAddress);
  }, [currentAddress]);

  // Pick Address from Google Geocode API
  const pickGeocodeAddress = useCallback(
    (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
      if (status === 'OK' && results && results[0]) {
        const pickedAddress = results[0];
        setCurrentAddress({
          lat: pickedAddress.geometry.location.lat(),
          lng: pickedAddress.geometry.location.lng(),
          location: pickedAddress.formatted_address,
        });
      } else {
        dispatch(
          addSnackbar({
            severity: 'error',
            message: 'Could not get the location information. Please try again',
          })
        );
      }
    },
    [dispatch]
  );

  // Adding click event on map to get the location
  useEffect(() => {
    if (map && window.google?.maps) {
      window.google.maps.event.clearListeners(map, 'click');
      map.addListener('click', (event: google.maps.MapMouseEvent) => {
        if (window.google?.maps) {
          new window.google.maps.Geocoder().geocode({ location: event.latLng }, pickGeocodeAddress);
        }
      });
    }
  }, [map, pickGeocodeAddress]);

  // Set to current location of the user
  const setToCurrentAddress = () => {
    if (window.navigator.geolocation && map) {
      window.navigator.geolocation.getCurrentPosition((position) => {
        if (window.google?.maps) {
          new window.google.maps.Geocoder().geocode(
            { location: { lat: position.coords.latitude, lng: position.coords.longitude } },
            pickGeocodeAddress
          );
        }
      });
    } else {
      dispatch(
        addSnackbar({
          severity: 'error',
          message: window.navigator.geolocation
            ? 'The Geolocation service failed. Please try again'
            : "Your browser doesn't support geolocation.",
        })
      );
    }
  };

  const markerLabel: string =
    currentAddress.location.trim().length > 0 ? currentAddress.location : 'Please pick a location';

  // Listening for changes in default address
  useEffect(() => {
    if (defaultAddress) {
      if (!_.isEqual(defaultAddressRef.current, defaultAddress)) {
        setCurrentAddress((prevValue) => ({
          location: defaultAddress.location,
          lat: _.defaultTo(defaultAddress.lat, prevValue.lat),
          lng: _.defaultTo(defaultAddress.lng, prevValue.lng),
        }));
      }
    } else {
      setCurrentAddress(siteSettingAddress);
    }
  }, [defaultAddress, siteSettingAddress]);

  useEffect(() => {
    defaultAddressRef.current = defaultAddress || siteSettingAddress;
  }, [defaultAddress, siteSettingAddress]);

  // Event to handle selection from dropdown using enter key
  useEffect(() => {
    const handleEnterKeyEventForSelection = (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.code === 'Enter') {
        event.preventDefault();
      }
    };
    let autocompleteInput: HTMLInputElement;
    if (autocompleteInputRef.current) {
      autocompleteInput = autocompleteInputRef.current;
      autocompleteInputRef.current.addEventListener('keydown', handleEnterKeyEventForSelection);
    }
    return () => {
      if (autocompleteInput) {
        autocompleteInput.removeEventListener('keydown', handleEnterKeyEventForSelection);
      }
    };
  }, []);

  return (
    <Box sx={{ mb: 2.5, ...sx }}>
      <TextInput
        sx={googleMapLocationStyles.locationField}
        {...textInputProps}
        FieldProps={{
          ...textInputProps.FieldProps,
          inputRef: autocompleteInputRef,
          InputProps: {
            onChange: (event) => setCurrentAddress((prevAddress) => ({ ...prevAddress, location: event.target.value })),
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Set to Current Location">
                  <IconButton onClick={setToCurrentAddress} edge="end" aria-label="Set to Current Location">
                    <AppIcon Icon={CurrentLocationIcon} sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          },
        }}
      />
      <input type="hidden" {...LatitudeInputProps} />
      <input type="hidden" {...LongitudeInputProps} />
      {isGoogleMapAuthError && (
        <Box sx={googleMapLocationStyles.mapError}>
          <Typography component="div" sx={{ mb: 2 }} variant="large" fontWeight="Medium" color="secondary">
            Oops! Something went wrong.
          </Typography>
          <Typography variant="small" fontWeight="Medium" color="secondary">
            Couldn&lsquo;t connect to Google Maps Service correctly. Please try again later.
          </Typography>
        </Box>
      )}
      <Box sx={[googleMapLocationStyles.map, { height: isGoogleMapAuthError ? 0 : '20rem' }]} ref={mapDivRef}>
        <GoogleMapMarker
          map={map}
          infoWindowContent={markerLabel}
          title={markerLabel}
          position={{ lat: currentAddress.lat, lng: currentAddress.lng }}
        />
      </Box>
    </Box>
  );
};
